import { useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Activity,
  LogOut,
  Shield,
  CreditCard,
  Package,
  Clock,
  UserCog,
  Workflow,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { adminApi } from '../../api/admin';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/customers', label: 'Customers', icon: Users, end: false },
  { to: '/admin/health', label: 'System Health', icon: Activity, end: false },
  { to: '/admin/administrators', label: 'Administrators', icon: UserCog, end: false },
  { to: '/admin/flow-templates', label: 'Flow Templates', icon: Workflow, end: false },
];

const billingNavItems = [
  { to: '/admin/billing', label: 'Billing Overview', icon: CreditCard, end: true },
  { to: '/admin/billing/products', label: 'Plans', icon: Package, end: false },
  { to: '/admin/billing/trials', label: 'Trials', icon: Clock, end: false },
];

function NavSection({
  title,
  items,
}: {
  title?: string;
  items: { to: string; label: string; icon: React.ElementType; end: boolean }[];
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 lg:block lg:space-y-1">
      {title && (
        <p className="mb-1 hidden px-3 text-xs font-semibold uppercase tracking-wider text-gray-400 lg:block">
          {title}
        </p>
      )}
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          className={({ isActive }) =>
            cn(
              'flex min-h-10 shrink-0 items-center gap-3 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-red-50 text-red-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
            )
          }
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {label}
        </NavLink>
      ))}
    </div>
  );
}

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const activeLink = navigationRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
    if (activeLink && typeof activeLink.scrollIntoView === 'function') {
      activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [location.pathname]);

  async function handleLogout() {
    localStorage.removeItem('adminToken');
    try {
      await adminApi.logout();
    } finally {
      void navigate('/admin/login');
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 lg:h-screen lg:flex-row">
      {/* Sidebar */}
      <aside className="flex w-full shrink-0 flex-col border-b border-gray-200 bg-white lg:h-screen lg:w-60 lg:border-b-0 lg:border-r">
        <div className="flex h-14 items-center justify-between gap-2 border-b border-gray-100 px-4">
          <span className="flex items-center gap-2 text-lg font-semibold">
            <Shield className="h-5 w-5 text-red-600" aria-hidden="true" />
            Grid2 Admin
          </span>
          <button type="button" onClick={handleLogout} className="flex min-h-10 items-center gap-2 rounded-xl px-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 lg:hidden">
            <LogOut className="h-4 w-4" aria-hidden="true" /> Logout
          </button>
        </div>

        <nav ref={navigationRef} className="flex gap-2 overflow-x-auto px-2 py-3 lg:flex-1 lg:block lg:space-y-6 lg:overflow-y-auto lg:py-4" aria-label="Admin navigation">
          <NavSection items={navItems} />
          <NavSection title="Billing" items={billingNavItems} />
        </nav>

        <div className="hidden border-t border-gray-100 p-2 lg:block">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center border-b border-gray-200 bg-white px-6">
          <h1 className="text-sm font-medium text-gray-500">
            Grid2 Admin Console
          </h1>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
